// ═══════════════════════════════════════════════════════════════
// KOTCH - AI LOGIC
// AI decision-making for computer opponents
// ═══════════════════════════════════════════════════════════════

import type {
  GameState,
  Player,
  PlayerId,
  LaneCommitment,
  CardInstance,
  Lane,
  AIPersonality,
  CardDefinition,
} from '../../types/kotch';
import { LANES, createEmptyCommitment } from '../../types/kotch';
import { AI_PERSONALITIES } from '../../data/kotch/gameConstants';
import { CARD_DEFINITIONS } from '../../data/kotch/cards';
import { shuffleDeck } from './deckUtils';

// ─────────────────────────────────────────────────────────────────
// MAIN AI DECISION FUNCTION
// ─────────────────────────────────────────────────────────────────

export function makeAICommitment(
  state: GameState,
  playerId: PlayerId
): LaneCommitment {
  const player = state.players[playerId];
  const personality = player.aiPersonality || 'medium';
  const config = AI_PERSONALITIES[personality];

  const isAttacker = state.currentAttackerId === playerId;
  const opponentCommitment = playerId === 1 ? state.player2Commitment : state.player1Commitment;

  if (isAttacker) {
    return makeAttackerCommitment(player, config, state);
  } else {
    return makeDefenderCommitment(player, config, opponentCommitment, state);
  }
}

// ─────────────────────────────────────────────────────────────────
// ATTACKER AI
// ─────────────────────────────────────────────────────────────────

function makeAttackerCommitment(
  player: Player,
  config: { smartPlayChance: number; conserveCardsChance: number; bluffChance: number },
  state: GameState
): LaneCommitment {
  const hand = [...player.hand];
  if (hand.length === 0) return createEmptyCommitment();

  // Check for pre-committed lanes (e.g. Motra persistent card)
  const existingCommitment = player.id === 1 ? state.player1Commitment : state.player2Commitment;
  const occupiedLanes = new Set<Lane>(
    LANES.filter(lane => existingCommitment[lane] !== null)
  );

  const commitment = createEmptyCommitment();
  const usedCards = new Set<string>();

  // Decide how many cards to commit
  const cardsToCommit = decideCardCount(hand.length, config, state);
  const targetLanes = selectAttackerLanes(cardsToCommit, config)
    .filter(lane => !occupiedLanes.has(lane));

  for (const lane of targetLanes) {
    const availableCards = hand.filter(c => !usedCards.has(c.instanceId));
    if (availableCards.length === 0) break;

    const selectedCard = selectCardForAttack(availableCards, lane, config, state);
    if (selectedCard) {
      commitment[lane] = selectedCard;
      usedCards.add(selectedCard.instanceId);
    }
  }

  return commitment;
}

function decideCardCount(
  handSize: number,
  config: { conserveCardsChance: number; smartPlayChance: number },
  state: GameState
): number {
  if (handSize === 0) return 0;

  // Must commit at least 1
  const minCommit = 1;
  const maxCommit = Math.min(3, handSize);

  // Smart AI considers game state
  if (Math.random() < config.smartPlayChance) {
    const battle = state.match.currentBattle;

    // If winning battle, conserve cards
    const p1Rounds = battle.player1RoundWins;
    const p2Rounds = battle.player2RoundWins;
    const aiPlayer = state.currentAttackerId;
    const aiWinning = (aiPlayer === 1 && p1Rounds > p2Rounds) ||
                      (aiPlayer === 2 && p2Rounds > p1Rounds);

    if (aiWinning && Math.random() < config.conserveCardsChance) {
      return Math.max(minCommit, Math.min(2, maxCommit));
    }

    // If losing, be more aggressive
    const aiLosing = (aiPlayer === 1 && p1Rounds < p2Rounds) ||
                     (aiPlayer === 2 && p2Rounds < p1Rounds);

    if (aiLosing) {
      return maxCommit;
    }
  }

  // Default: commit 2-3 cards
  return Math.min(2 + Math.floor(Math.random() * 2), maxCommit);
}

function selectAttackerLanes(count: number, config: { smartPlayChance: number }): Lane[] {
  const lanes: Lane[] = [...LANES];

  if (Math.random() < config.smartPlayChance) {
    // Prioritize center, then flanks
    const priorityOrder: Lane[] = ['center', 'left', 'right'];
    return priorityOrder.slice(0, count);
  }

  // Random selection
  return shuffleDeck(lanes).slice(0, count);
}

function selectCardForAttack(
  cards: CardInstance[],
  lane: Lane,
  config: { smartPlayChance: number; bluffChance: number },
  state: GameState
): CardInstance | null {
  if (cards.length === 0) return null;

  if (Math.random() < config.smartPlayChance) {
    // Categorize cards
    const basicCards = cards.filter(c => getCardDef(c).category === 'basic');
    const specialCards = cards.filter(c => getCardDef(c).category === 'special');
    const knights = specialCards.filter(c => getCardDef(c).type === 'knight');
    const spies = specialCards.filter(c => getCardDef(c).type === 'spy');

    // Center lane is important - use strong cards
    if (lane === 'center') {
      if (knights.length > 0 && Math.random() > 0.5) {
        return knights[0];
      }
      // Use high-power basic
      const sortedBasic = [...basicCards].sort((a, b) =>
        getCardDef(b).power - getCardDef(a).power
      );
      if (sortedBasic.length > 0) {
        return sortedBasic[0];
      }
    }

    // Consider bluffing with spy
    if (spies.length > 0 && Math.random() < config.bluffChance) {
      return spies[0];
    }

    // Use mid-range basic cards
    if (basicCards.length > 0) {
      const midIndex = Math.floor(basicCards.length / 2);
      return basicCards[midIndex];
    }
  }

  // Random selection
  return cards[Math.floor(Math.random() * cards.length)];
}

// ─────────────────────────────────────────────────────────────────
// DEFENDER AI
// ─────────────────────────────────────────────────────────────────

function makeDefenderCommitment(
  player: Player,
  config: { smartPlayChance: number; bluffChance: number; conserveCardsChance: number },
  attackerCommitment: LaneCommitment,
  state: GameState
): LaneCommitment {
  const hand = [...player.hand];
  if (hand.length === 0) return createEmptyCommitment();

  // Check for pre-committed lanes (e.g. Motra persistent card)
  const existingCommitment = player.id === 1 ? state.player1Commitment : state.player2Commitment;
  const occupiedLanes = new Set<Lane>(
    LANES.filter(lane => existingCommitment[lane] !== null)
  );

  const commitment = createEmptyCommitment();
  const usedCards = new Set<string>();

  // See which lanes attacker committed to
  const attackedLanes = LANES.filter(lane => attackerCommitment[lane] !== null);
  const unattackedLanes = LANES.filter(lane => attackerCommitment[lane] === null);

  // Decide which lanes to defend (exclude pre-committed lanes)
  const lanesToDefend = selectDefenderLanes(
    attackedLanes,
    unattackedLanes,
    hand.length,
    config
  ).filter(lane => !occupiedLanes.has(lane));

  for (const lane of lanesToDefend) {
    const availableCards = hand.filter(c => !usedCards.has(c.instanceId));
    if (availableCards.length === 0) break;

    const isAttackedLane = attackedLanes.includes(lane);
    const selectedCard = selectCardForDefense(
      availableCards,
      lane,
      isAttackedLane,
      config,
      state
    );

    if (selectedCard) {
      commitment[lane] = selectedCard;
      usedCards.add(selectedCard.instanceId);
    }
  }

  return commitment;
}

function selectDefenderLanes(
  attackedLanes: Lane[],
  unattackedLanes: Lane[],
  handSize: number,
  config: { smartPlayChance: number; conserveCardsChance: number }
): Lane[] {
  const result: Lane[] = [];

  if (Math.random() < config.smartPlayChance) {
    // Smart defender focuses on attacked lanes
    // Prioritize center if attacked
    const prioritizedAttacked = [...attackedLanes].sort((a, b) => {
      if (a === 'center') return -1;
      if (b === 'center') return 1;
      return 0;
    });

    // Defend attacked lanes first
    for (const lane of prioritizedAttacked) {
      if (result.length < handSize && result.length < 3) {
        result.push(lane);
      }
    }

    // Consider taking unattacked lanes for free wins
    if (result.length < 3 && result.length < handSize) {
      const shouldTakeFreeLane = Math.random() > config.conserveCardsChance;
      if (shouldTakeFreeLane && unattackedLanes.length > 0) {
        result.push(unattackedLanes[0]);
      }
    }

    return result;
  }

  // Simple AI: just defend what's attacked
  return attackedLanes.slice(0, Math.min(handSize, 3));
}

function selectCardForDefense(
  cards: CardInstance[],
  lane: Lane,
  isAttackedLane: boolean,
  config: { smartPlayChance: number; bluffChance: number },
  state: GameState
): CardInstance | null {
  if (cards.length === 0) return null;

  if (Math.random() < config.smartPlayChance) {
    const basicCards = cards.filter(c => getCardDef(c).category === 'basic');
    const knights = cards.filter(c => getCardDef(c).type === 'knight');
    const spies = cards.filter(c => getCardDef(c).type === 'spy');

    if (!isAttackedLane) {
      // Unattacked lane - use weak card for free win
      const sortedBasic = [...basicCards].sort((a, b) =>
        getCardDef(a).power - getCardDef(b).power
      );
      if (sortedBasic.length > 0) {
        return sortedBasic[0];
      }
    }

    // Attacked lane - strategic choice
    if (lane === 'center') {
      // Important lane - consider knight
      if (knights.length > 0 && Math.random() > 0.3) {
        return knights[0];
      }
    }

    // Consider spy if opponent might use knight
    if (spies.length > 0 && Math.random() < config.bluffChance * 0.5) {
      return spies[0];
    }

    // Use varied basic types for coverage
    if (basicCards.length > 0) {
      // Try to spread types
      const typeGroups = groupByType(basicCards);
      const types = Object.keys(typeGroups);
      const selectedType = types[Math.floor(Math.random() * types.length)];
      const typeCards = typeGroups[selectedType];

      // Use mid-high power
      typeCards.sort((a, b) => getCardDef(b).power - getCardDef(a).power);
      const index = Math.min(1, typeCards.length - 1);
      return typeCards[index];
    }
  }

  // Random selection
  return cards[Math.floor(Math.random() * cards.length)];
}

// ─────────────────────────────────────────────────────────────────
// HELPER FUNCTIONS
// ─────────────────────────────────────────────────────────────────

function getCardDef(instance: CardInstance): CardDefinition {
  return CARD_DEFINITIONS[instance.definitionId];
}

function groupByType(cards: CardInstance[]): Record<string, CardInstance[]> {
  const groups: Record<string, CardInstance[]> = {};

  cards.forEach(card => {
    const type = getCardDef(card).type;
    if (!groups[type]) {
      groups[type] = [];
    }
    groups[type].push(card);
  });

  return groups;
}

// ─────────────────────────────────────────────────────────────────
// AI TIMING
// ─────────────────────────────────────────────────────────────────

export function getAIThinkingDelay(personality: AIPersonality): number {
  switch (personality) {
    case 'easy':
      return 800;
    case 'medium':
      return 1200;
    case 'hard':
      return 1500;
    default:
      return 1000;
  }
}
